import './polyfills'
import { createApp } from 'vue'
import * as Sentry from '@sentry/vue'
import { createPinia } from 'pinia'
import App from './App.vue'
import router, { loginForPath } from './router'
import { i18n, currentLocale } from '@/i18n'
import { installLocaleLinkSync } from '@/i18n/shared-locale'
import { AUTH_URL, LANDING_URL } from '@/constants/app'
import { toast } from '@/components/ui/sonner'
import { onSessionExpired } from '@/services/http'
import { useAuthStore } from '@/stores/auth.store'
import { registerPwa, unregisterPwaForAdmin } from '@/lib/pwa'
import { captureInstalledPwaRoleFromUrl } from '@/lib/pwa-role'
import './assets/main.css'

// Links to the landing page and the sign-in app carry the language chosen here.
installLocaleLinkSync([LANDING_URL, AUTH_URL], currentLocale)

captureInstalledPwaRoleFromUrl()
void unregisterPwaForAdmin().then(() => registerPwa())

const app = createApp(App)
const sentryDsn = import.meta.env.VITE_SENTRY_DSN?.trim()

if (sentryDsn) {
  Sentry.init({
    app,
    dsn: sentryDsn,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT?.trim() || import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE?.trim() || undefined,
  })
}

app.use(createPinia()).use(router).use(i18n).mount('#app')

// A session that expired or was revoked behind the person's back: the server refuses every request,
// and a page that only shows the error leaves them stuck. Drop the session and open the sign-in page
// of the area they were in, the way any site does. Public pages (tracking, the home page) just carry
// on without a session.
onSessionExpired(() => {
  useAuthStore().clearSession()

  const route = router.currentRoute.value
  const needsSignIn = route.matched.some((record) => ((record.meta.roles as unknown[] | undefined) ?? []).length > 0)
  if (!needsSignIn) return

  toast.error(i18n.global.t('errors.unauthenticated'))
  void router.replace(loginForPath(route.path))
})
